import React, { useEffect, useRef, useMemo, memo, useCallback } from "react";
import Hls from "hls.js";
import { cn } from "../../lib/utils";
import { getPreInitializedHls } from "./videoPreloader";

const isDev = process.env.NODE_ENV === "development";
const logError = isDev ? console.error : () => {};
const logWarn = isDev ? console.warn : () => {};

// Track global video playing state to prevent duplicate plays
export const globalPlayingVideos = new Set<string>();

interface VideoPlayerV2Props
  extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  poster?: string;
  autoPlay?: boolean;
  controls?: boolean;
  muted?: boolean;
  showGradient?: boolean;
  isDescriptionExpanded?: boolean;
}

function VideoPlayerInternal(
  {
    src,
    poster,
    autoPlay = false,
    controls,
    muted,
    style = {},
    className = "",
    showGradient = true,
    isDescriptionExpanded = false,
    ...rest
  }: VideoPlayerV2Props,
  forwardedRef: React.ForwardedRef<HTMLVideoElement>
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const currentSrcRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  const videoMuted = useMemo(() => muted ?? false, [muted]);
  const previousAutoPlayRef = useRef<boolean>(autoPlay);

  // Optimized tryPlay function - no RAF delays, direct play
  // This function uses the current autoPlay value from closure, not a ref
  const tryPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    const videoSrc = video.src || src;

    // Critical: Prevent duplicate play attempts for the same video source
    if (globalPlayingVideos.has(videoSrc)) {
      return;
    }

    if (video.paused && autoPlay) {
      // Mark as playing globally BEFORE attempting to play
      globalPlayingVideos.add(videoSrc);

      video
        .play()
        .then(() => {
          // Video started playing successfully
        })
        .catch((err) => {
          // Remove from global playing set on failure
          globalPlayingVideos.delete(videoSrc);
          logError("Autoplay failed:", err);
        });
    }
  }, [src, autoPlay]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !src) {
      return () => {}; // Empty cleanup for early return
    }

    // Clean up global playing state when video ends
    const handleVideoEnd = () => {
      const videoSrc = video.src || src;
      if (globalPlayingVideos.has(videoSrc)) {
        globalPlayingVideos.delete(videoSrc);
      }
    };

    video.addEventListener("ended", handleVideoEnd);

    // Re-initialize when src changes
    const srcChanged = currentSrcRef.current !== src;
    if (srcChanged) {
      // Clean up global playing state for old src
      if (currentSrcRef.current && globalPlayingVideos.has(currentSrcRef.current)) {
        globalPlayingVideos.delete(currentSrcRef.current);
      }

      // Clean up old HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      currentSrcRef.current = src;
      isInitializedRef.current = false;
    }

    const isHls = /\.m3u8($|\?)/i.test(src);

    // Strategy 1 & 4: Remove RAF delays, eliminate unnecessary video.load() calls
    // Strategy 3 & 5: Optimize HLS initialization with pre-initialized instances
    // Strategy 7: Optimize buffer settings for low latency

    if (isHls) {
      // Try to get pre-initialized HLS instance first (Strategy 3 & 10)
      const preInitializedHls = getPreInitializedHls(src);
      
      if (preInitializedHls) {
        // Use pre-warmed HLS instance - attach to our video element
        hlsRef.current = preInitializedHls;
        preInitializedHls.attachMedia(video);

        // Strategy 5: Start loading immediately (manifest already parsed)
        preInitializedHls.startLoad();

        // Video is ready - play immediately if autoplay
        // Always try to play when autoPlay is true, regardless of initialization state
        if (autoPlay) {
          // Strategy 1: Direct play, no RAF delay
          tryPlay();
        }
        isInitializedRef.current = true;

        return () => {
          if (hlsRef.current) {
            hlsRef.current.detachMedia();
            // Don't destroy - it might be reused
          }
          video.removeEventListener("ended", handleVideoEnd);
        };
      }

      // Native HLS (Safari) - Strategy 8: Use native preload
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Strategy 4: Only call load() if src actually changed
        if (video.src !== src) {
          video.src = src;
          video.preload = "auto"; // Strategy 8: Native preload
          video.load();
        }

        // Strategy 1: Direct play, no RAF delay
        // Always try to play when autoPlay is true, regardless of initialization state
        if (autoPlay) {
          tryPlay();
        }
        isInitializedRef.current = true;

        return () => {
          video.removeEventListener("ended", handleVideoEnd);
        };
      }

      // hls.js fallback - Strategy 3, 5, 7: Optimized HLS config
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          maxBufferLength: 1, // Strategy 7: Reduced for low latency
          maxBufferSize: 0,
          maxBufferHole: 0.3, // Strategy 7: Reduced from 0.5
          lowLatencyMode: true,
          startLevel: -1, // Strategy 5: Auto-select best quality
          capLevelToPlayerSize: true,
        });

        hls.loadSource(src);
        hls.attachMedia(video);
        hlsRef.current = hls;

        // Strategy 5: Start loading immediately, don't wait for manifest
        hls.startLoad();

        const onManifest = () => {
          // Strategy 1: Direct play on manifest parsed, no RAF delay
          // Always try to play when autoPlay is true, regardless of initialization state
          if (autoPlay) {
            tryPlay();
          }
          isInitializedRef.current = true;
        };

        hls.on(Hls.Events.MANIFEST_PARSED, onManifest);

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                break;
            }
          }
        });

        return () => {
          hls.off(Hls.Events.MANIFEST_PARSED, onManifest);
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          isInitializedRef.current = false;
          video.removeEventListener("ended", handleVideoEnd);
        };
      }

      logWarn("HLS not supported - no playback method available");
      isInitializedRef.current = true;
      return () => {
        video.removeEventListener("ended", handleVideoEnd);
      };
    }

    // Non-HLS (MP4, WebM, etc.) - Strategy 1, 4, 8
    // Strategy 4: Only call load() if src actually changed
    if (video.src !== src) {
      video.src = src;
      video.preload = "auto"; // Strategy 8: Native preload
      video.load();
    }

    // Strategy 1: Direct play, no RAF delay
    // Always try to play when autoPlay is true, regardless of initialization state
    if (autoPlay) {
      tryPlay();
    }
    isInitializedRef.current = true;

    // Handle autoplay if video becomes ready after initial attempt
    const handleCanPlay = () => {
      if (autoPlay && video.paused) {
        tryPlay();
      }
    };

    video.addEventListener("canplay", handleCanPlay);

    return () => {
      video.removeEventListener("ended", handleVideoEnd);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [src, autoPlay, tryPlay, forwardedRef]);

  // Separate useEffect to handle autoPlay prop changes
  // This ensures autoplay works when swiping to videos (autoPlay changes from false to true)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    // Only trigger when autoPlay changes from false to true
    const autoPlayChanged = previousAutoPlayRef.current !== autoPlay;
    previousAutoPlayRef.current = autoPlay;

    if (autoPlayChanged && autoPlay) {
      // Wait a tiny bit to ensure video/HLS is ready
      const timeoutId = setTimeout(() => {
        tryPlay();
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [autoPlay, src, tryPlay]);

  // Strategy 9: Optimized ref handling with useMemo
  const handleRef = useMemo(
    () => (node: HTMLVideoElement | null) => {
      videoRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
      }
    },
    [forwardedRef]
  );

  // Strategy 9: Memoized gradient classes to prevent re-renders
  const baseGradientClass = useMemo(
    () =>
      "pointer-events-none absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.45)] via-[rgba(0,0,0,0.15)] to-transparent",
    []
  );

  const expandedGradientClass = useMemo(
    () =>
      cn(
        "pointer-events-none absolute inset-0",
        "bg-gradient-to-t from-[rgba(0,0,0,0.75)] via-[rgba(0,0,0,0.35)] to-transparent",
        "transition-opacity duration-500 ease-in-out",
        isDescriptionExpanded ? "opacity-100" : "opacity-0"
      ),
    [isDescriptionExpanded]
  );

  return (
    <div className={cn("relative", className)}>
      <video
        ref={handleRef}
        poster={poster}
        controls={controls}
        playsInline
        muted={videoMuted}
        loop
        preload="auto" // Strategy 8: Native preload attribute
        style={style}
        className="hls-video bg-black w-full h-full object-cover"
        {...rest}
      />

      {showGradient && <div aria-hidden className={baseGradientClass} />}
      {showGradient && <div aria-hidden className={expandedGradientClass} />}
    </div>
  );
}

const VideoPlayerV2Forwarded = React.forwardRef<HTMLVideoElement, VideoPlayerV2Props>(
  VideoPlayerInternal
);

VideoPlayerV2Forwarded.displayName = "VideoPlayerV2";

// Strategy 9: Memoize component to prevent unnecessary re-renders
export default memo(VideoPlayerV2Forwarded);
